import isEmpty from "lodash/isEmpty";
import some from "lodash/some";
import { BookingDetailActivity } from "PFApp/booking/components/booking_detail_activity";
import { isGroupOption } from "PFApp/booking/components/booking_form/workforce_member_select/workforce_member_select.utils";
import { DetailsPanelData, DetailsPanelSource } from "PFApp/booking/components/details_panel";
import { setTimeToMidnight } from "PFApp/booking/parts/overview/calendar/calendar.utils";
import SidePanel from "PFComponents/side_panel/side_panel";
import { useBooking } from "PFCore/hooks/queries";
import { OverbookedDates, OverbookingsManagementMode } from "PFCore/services/bookings_suggested_changes";
import { BookingCategory } from "PFTypes";
import { Dispatch, RefObject, SetStateAction, useEffect } from "react";
import { useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useBookingActivityContext } from "../../parts/providers/booking_activity_context_provider";
import { AvailabilityInfo } from "./availability_info";
import css from "./booking_form.module.scss";
import {
  BookingDataItem,
  BookingFormData,
  BookingFormDataCreateMode,
  BookingFormDataEditOrCloneMode,
  BookingFormMode
} from "./booking_form.types";
import { getHeaderTitleTranslationKey, isEditMode } from "./booking_form.utils";
import { BookingFormGlobalInputs } from "./booking_form_global_inputs";
import { BookingFormBasicProfile, BookingFormValues } from "./booking_form_values.types";
import { BookingRangeColumn } from "./booking_range_column";
import { BookingTypeSelector } from "./booking_type_selector";
import { EngagementSelector, useShouldDisplayEngagementSelect } from "./engagement_selector";
import { Footer } from "./footer/footer";
import { useAreBookingPhasesValid } from "./use_are_booking_phases_valid";
import { usePotentialWarnings } from "./use_potential_warnings";
import { WorkforceMemberSelect } from "./workforce_member_select";

export type BookingFormProps = {
  show: boolean;
  zIndex?: number;
  onClose?: () => void;
  onOpenOverbookingsManagementModal: (
    mode: OverbookingsManagementMode,
    overbookedDates: OverbookedDates
  ) => void;
  initialData?: BookingFormData;
  mode: BookingFormMode;
  source?: DetailsPanelSource;
  bookingModuleDetails: DetailsPanelData["bookingModuleDetails"];
  loadingState: Record<string, boolean>;
  setLoadingState: Dispatch<SetStateAction<Record<string, boolean>>>;
  activeBookingIndex: number | undefined;
  setActiveBookingIndex: (index: number | undefined) => void;
  disableEntranceAnimation?: boolean;
  initialBookingsRef: RefObject<BookingDataItem[] | null>;
};

export const BookingForm = ({
  show,
  zIndex,
  onClose,
  initialData = {},
  mode,
  onOpenOverbookingsManagementModal,
  source,
  bookingModuleDetails,
  loadingState,
  setLoadingState,
  activeBookingIndex,
  setActiveBookingIndex,
  disableEntranceAnimation = false,
  initialBookingsRef
}: BookingFormProps) => {
  const { t } = useTranslation(["bookingModule", "translation"]);
  const { activity, parentActivity, isLoading: isActivityLoading } = useBookingActivityContext();
  const areBookingsValid = useAreBookingPhasesValid(mode);

  const isCreateMode = mode === BookingFormMode.Create;
  const bookingId = isCreateMode ? undefined : (initialData as BookingFormDataEditOrCloneMode).bookingId;
  const { data: initialBooking } = useBooking(bookingId, { enabled: !!bookingId });

  const [workforceMember, bookings, activityId, globalCategory] = useWatch<
    BookingFormValues,
    ["workforceMember", "bookings", "activityId", "globalCategory"]
  >({
    name: ["workforceMember", "bookings", "activityId", "globalCategory"]
  });
  const { isSubmitting } = useFormState();

  const shouldDisplayEngagementSelect = useShouldDisplayEngagementSelect();

  const { overbookings, misalignments, overbookingIntersectionsDates } = usePotentialWarnings({
    mode,
    setLoadingState,
    initialBookingsRef
  });

  const sidePanelTitleKey = getHeaderTitleTranslationKey(mode);

  const isBookingFormLoading = !isEmpty(loadingState) && some(loadingState, (value) => !!value);
  const isSubmitDisabled = !areBookingsValid || isSubmitting || isBookingFormLoading;

  useEffect(() => {
    if (isEditMode(mode)) {
      return;
    }

    const categoryAffectsAvailability = isCreateMode
      ? (globalCategory as BookingCategory)?.affects_availability
      : (bookings ?? [])
          .map((booking) =>
            booking?.category ? (booking?.category as BookingCategory)?.affects_availability : false
          )
          .reduce((acc, curr) => acc || curr, false);

    if (
      (overbookingIntersectionsDates ?? []).length > 0 &&
      bookings.length > 0 &&
      categoryAffectsAvailability
    ) {
      const overbookedBookings = bookings.map((booking) =>
        isCreateMode ? { ...booking, category: globalCategory } : booking
      );
      if (!isGroupOption(workforceMember)) {
        bookingModuleDetails?.updateOverbookedBookings?.({
          profileId: workforceMember?.id,
          activityId,
          overbookedBookings
        });
      }
      const currentBooking = bookings[activeBookingIndex || 0];
      if (bookingModuleDetails?.goToDate && currentBooking) {
        bookingModuleDetails?.goToDate(setTimeToMidnight(new Date(currentBooking.startDate)));
      }
    } else {
      bookingModuleDetails?.updateOverbookedBookings?.(null);
    }
  }, [mode, overbookingIntersectionsDates, bookings, activeBookingIndex]);

  return (
    <SidePanel
      qaId="BookingFormSidePanel"
      show={show}
      zIndex={zIndex}
      onClose={onClose}
      title={t(sidePanelTitleKey)}
      disableEntranceAnimation={disableEntranceAnimation}
      footerRenderer={({ onSidePanelClose }) => (
        <Footer
          profileId={(workforceMember as BookingFormBasicProfile)?.id}
          activity={activity}
          misalignments={misalignments ?? []}
          overbookings={overbookings ?? []}
          overbookingIntersectionsDates={overbookingIntersectionsDates}
          onClose={onSidePanelClose}
          isSubmitDisabled={isSubmitDisabled}
          isLoading={isBookingFormLoading}
          mode={mode}
          onOpenOverbookingsManagementModal={onOpenOverbookingsManagementModal}
          source={source}
        />
      )}
    >
      <div className={css.content}>
        <div className={css.section}>
          <WorkforceMemberSelect
            initialData={initialData}
            mode={mode}
            booking={initialBooking}
            source={source}
            loadingState={loadingState}
            setLoadingState={setLoadingState}
            initialActivity={activity}
          />
          {!!activity && !shouldDisplayEngagementSelect && (
            <BookingDetailActivity
              activity={activity}
              parentActivity={parentActivity}
              isLoading={isActivityLoading}
              source={source}
            />
          )}
          {activity?.metadata.availability && (
            <AvailabilityInfo availability={activity.metadata.availability} />
          )}
          {parentActivity?.id && (
            <BookingDetailActivity activity={parentActivity} isLoading={isActivityLoading} source={source} />
          )}
          {shouldDisplayEngagementSelect && (
            <EngagementSelector
              mode={mode}
              hasInitialBookingDates={!isEmpty((initialData as BookingFormDataCreateMode).bookings)}
            />
          )}
        </div>
        <BookingTypeSelector initialData={initialData} mode={mode} />
        <BookingRangeColumn
          initialData={initialData}
          mode={mode}
          initialBooking={initialBooking}
          activeBookingIndex={activeBookingIndex}
          setActiveBookingIndex={setActiveBookingIndex}
          loadingState={loadingState}
          setLoadingState={setLoadingState}
          initialBookingsRef={initialBookingsRef}
        />
        {isCreateMode && <BookingFormGlobalInputs canSelectEngagement={shouldDisplayEngagementSelect} />}
      </div>
    </SidePanel>
  );
};
